import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Check, X, Trash2, Phone, Mail, MapPin, Plane, Calendar, RefreshCw, DollarSign, Users, KeyRound, ClipboardList, UserPlus, BarChart3, Image as ImageIcon, Pencil, Moon, Save } from "lucide-react";
import { BRAND } from "../mock";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "ftz_admin_token";

const STATUS = {
  pending: { label: "Ausstehend", cls: "bg-[#FFF4E5] text-[#B26A00]" },
  confirmed: { label: "Bestätigt", cls: "bg-[#E7F8EE] text-[#1B7F45]" },
  cancelled: { label: "Storniert", cls: "bg-[#FDECEC] text-[#C0392B]" },
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [fees, setFees] = useState({ nightPercent: 15, nightStart: 22, nightEnd: 6 });
  const [images, setImages] = useState({ hero: "", vehicles: {} });
  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);

  const authHeaders = useCallback(() => ({ "Content-Type": "application/json", Authorization: `Bearer ${token}` }), [token]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setBookings([]); setDrivers([]); setPricing([]); setAnalytics(null);
  }, []);

  const api = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, { ...opts, headers: { ...authHeaders(), ...(opts.headers || {}) } });
    if (res.status === 401) { logout(); toast.error("Sitzung abgelaufen."); throw new Error("401"); }
    return res;
  }, [authHeaders, logout]);

  const loadAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [bRes, dRes, pRes, fRes, iRes, aRes] = await Promise.all([
        api("/api/admin/bookings"),
        api("/api/admin/drivers"),
        fetch(`${API}/api/pricing`),
        fetch(`${API}/api/fees`),
        fetch(`${API}/api/images`),
        api("/api/admin/analytics"),
      ]);
      setBookings(await bRes.json());
      setDrivers(await dRes.json());
      setPricing(await pRes.json());
      setFees(await fRes.json());
      setImages(await iRes.json());
      setAnalytics(await aRes.json());
    } catch (e) {
      if (e.message !== "401") toast.error("Fehler beim Laden.");
    } finally {
      setLoading(false);
    }
  }, [token, api]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await fetch(`${API}/api/auth/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Login fehlgeschlagen");
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Willkommen zurück!");
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Login fehlgeschlagen");
    } finally {
      setLoggingIn(false);
    }
  };

  const setStatus = async (id, status) => {
    setBusy(id + status);
    try {
      const res = await api(`/api/admin/bookings/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) });
      if (!res.ok) throw new Error();
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(status === "confirmed" ? "Bestätigt – E-Mail an Kunde gesendet." : "Storniert – E-Mail an Kunde gesendet.");
    } catch (e) { toast.error("Aktion fehlgeschlagen."); } finally { setBusy(null); }
  };

  const removeBooking = async (id) => {
    if (!window.confirm("Diese Buchung wirklich löschen?")) return;
    setBusy(id + "del");
    try {
      const res = await api(`/api/admin/bookings/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast.success("Buchung gelöscht.");
    } catch (e) { toast.error("Löschen fehlgeschlagen."); } finally { setBusy(null); }
  };

  const assign = async (id, driver_id) => {
    if (!driver_id) return;
    setBusy(id + "assign");
    try {
      const res = await api(`/api/admin/bookings/${id}/assign`, { method: "PATCH", body: JSON.stringify({ driver_id }) });
      const data = await res.json();
      if (!res.ok) throw new Error();
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, driver: data.driver } : b)));
      toast.success(`Fahrer zugewiesen – E-Mail an ${data.driver.name} gesendet.`);
    } catch (e) { toast.error("Zuweisung fehlgeschlagen."); } finally { setBusy(null); }
  };

  const saveEdit = async (id, changes) => {
    setBusy(id + "edit");
    try {
      const res = await api(`/api/admin/bookings/${id}`, { method: "PATCH", body: JSON.stringify(changes) });
      const data = await res.json();
      if (!res.ok) throw new Error();
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, ...data } : b)));
      setEditing(null);
      toast.success("Buchung aktualisiert.");
    } catch (e) { toast.error("Aktualisierung fehlgeschlagen."); } finally { setBusy(null); }
  };

  // ---------- Login screen ----------
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B1533] flex items-center justify-center px-4">
        <form onSubmit={login} className="w-full max-w-sm bg-white rounded-3xl p-8 elev-lg" data-testid="admin-login-form">
          <div className="flex justify-center mb-6"><img src={BRAND.logo} alt="Logo" className="h-12 bg-white rounded-xl px-3 py-1" /></div>
          <h1 className="font-heading font-bold text-2xl text-[#0B1533] text-center">Admin-Anmeldung</h1>
          <p className="font-body text-sm text-[#5A607A] text-center mt-1 mb-6">Buchungen verwalten</p>
          <label className="font-body text-xs text-[#5A607A] mb-1.5 block">E-Mail</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email" className="w-full bg-white border border-[#E9E5DE] rounded-2xl px-4 py-3.5 font-body text-sm mb-4 focus:border-[#2B4BFF] focus:outline-none" />
          <label className="font-body text-xs text-[#5A607A] mb-1.5 block">Passwort</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password" className="w-full bg-white border border-[#E9E5DE] rounded-2xl px-4 py-3.5 font-body text-sm mb-6 focus:border-[#2B4BFF] focus:outline-none" />
          <button type="submit" disabled={loggingIn} data-testid="admin-login-btn" className="w-full inline-flex items-center justify-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full py-3.5 hover:bg-[#1E38D8] transition-colors disabled:opacity-50">
            {loggingIn ? <Loader2 size={18} className="animate-spin" /> : "Anmelden"}
          </button>
        </form>
      </div>
    );
  }

  const counts = { all: bookings.length, pending: bookings.filter((b) => b.status === "pending").length, confirmed: bookings.filter((b) => b.status === "confirmed").length, cancelled: bookings.filter((b) => b.status === "cancelled").length };
  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  const TABS = [
    { id: "bookings", label: "Buchungen", icon: ClipboardList },
    { id: "analytics", label: "Statistik", icon: BarChart3 },
    { id: "calendar", label: "Kalender", icon: Calendar },
    { id: "pricing", label: "Preise", icon: DollarSign },
    { id: "images", label: "Bilder", icon: ImageIcon },
    { id: "drivers", label: "Fahrer", icon: Users },
    { id: "settings", label: "Passwort", icon: KeyRound },
  ];

  return (
    <div className="min-h-screen bg-[#F5F2ED]">
      <header className="bg-[#0B1533] text-white sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="Logo" className="h-9 bg-white rounded-lg px-2 py-1" />
            <span className="font-heading font-semibold hidden sm:block">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadAll} className="inline-flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm hover:bg-white/10 transition-colors" data-testid="admin-refresh"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> <span className="hidden sm:inline">Aktualisieren</span></button>
            <button onClick={logout} className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 text-sm hover:bg-white/20 transition-colors" data-testid="admin-logout"><LogOut size={15} /> <span className="hidden sm:inline">Abmelden</span></button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`} className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-body font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? "border-white text-white" : "border-transparent text-white/50 hover:text-white/80"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {tab === "bookings" && (
          <BookingsTab {...{ counts, filter, setFilter, filtered, loading, bookings, drivers, busy, setStatus, removeBooking, assign, setEditing, STATUS }} />
        )}
        {tab === "analytics" && <AnalyticsTab analytics={analytics} loading={loading} />}
        {tab === "calendar" && <CalendarTab bookings={bookings} STATUS={STATUS} />}
        {tab === "pricing" && <PricingTab pricing={pricing} setPricing={setPricing} fees={fees} setFees={setFees} api={api} />}
        {tab === "images" && <ImagesTab images={images} setImages={setImages} pricing={pricing} api={api} />}
        {tab === "drivers" && <DriversTab drivers={drivers} setDrivers={setDrivers} api={api} />}
        {tab === "settings" && <SettingsTab api={api} />}
      </div>
      {editing && <EditBookingModal booking={editing} onClose={() => setEditing(null)} onSave={saveEdit} busy={busy} pricing={pricing} />}
    </div>
  );
};

const Field = ({ label, ...rest }) => (
  <div><label className="font-body text-xs text-[#5A607A] mb-1 block">{label}</label><input {...rest} className="w-full bg-white border border-[#E9E5DE] rounded-xl px-3 py-2.5 font-body text-sm focus:border-[#2B4BFF] focus:outline-none" /></div>
);

const BookingsTab = ({ counts, filter, setFilter, filtered, loading, bookings, drivers, busy, setStatus, removeBooking, assign, setEditing, STATUS }) => (
  <>
    <div className="flex flex-wrap gap-2 mb-6">
      {["all", "pending", "confirmed", "cancelled"].map((f) => (
        <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`} className={`font-body text-sm rounded-full px-4 py-2 border transition-colors ${filter === f ? "bg-[#0B1533] text-white border-[#0B1533]" : "bg-white text-[#5A607A] border-[#E9E5DE] hover:border-[#0B1533]"}`}>
          {f === "all" ? "Alle" : STATUS[f].label} ({counts[f]})
        </button>
      ))}
    </div>
    {loading && bookings.length === 0 ? (
      <p className="font-body text-[#5A607A] flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Lädt …</p>
    ) : filtered.length === 0 ? (
      <div className="bg-white rounded-2xl border border-[#E9E5DE] p-12 text-center font-body text-[#5A607A]">Keine Buchungen in dieser Kategorie.</div>
    ) : (
      <div className="grid gap-4">
        {filtered.map((b) => (
          <div key={b.id} className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6" data-testid={`booking-${b.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-heading font-semibold text-lg text-[#0B1533]">{b.name}</h3>
                  <span className={`font-body text-xs font-semibold rounded-full px-3 py-1 ${STATUS[b.status]?.cls || ""}`}>{STATUS[b.status]?.label || b.status}</span>
                  {b.payment_status && (
                    <span data-testid={`payment-badge-${b.id}`} className={`font-body text-xs font-semibold rounded-full px-3 py-1 ${b.payment_status === "PAID" ? "bg-[#E7F8EE] text-[#1B7F45]" : b.payment_status === "PENDING" ? "bg-[#FFF6E5] text-[#B77900]" : "bg-[#FDECEC] text-[#C0392B]"}`}>
                      {b.payment_status === "PAID" ? "💳 Bezahlt" : b.payment_status === "PENDING" ? "⏳ Zahlung offen" : `Zahlung: ${b.payment_status}`}
                    </span>
                  )}
                  {b.price && <span className="font-heading font-bold text-[#0B1533]">CHF {b.price}.–</span>}
                </div>
                <p className="font-body text-xs text-[#9CA0B0] mt-1">Nr. {b.ref || b.id} · {b.created_at ? new Date(b.created_at).toLocaleString("de-CH") : ""}</p>
              </div>
              <div className="flex gap-2">
                <button disabled={b.status === "confirmed" || busy} onClick={() => setStatus(b.id, "confirmed")} data-testid={`confirm-${b.id}`} className="inline-flex items-center gap-1.5 bg-[#25D366] text-white font-body text-sm font-semibold rounded-full px-4 py-2 hover:opacity-90 transition-opacity disabled:opacity-40">{busy === b.id + "confirmed" ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Bestätigen</button>
                <button disabled={b.status === "cancelled" || busy} onClick={() => setStatus(b.id, "cancelled")} data-testid={`cancel-${b.id}`} className="inline-flex items-center gap-1.5 bg-[#FDECEC] text-[#C0392B] font-body text-sm font-semibold rounded-full px-4 py-2 hover:bg-[#f9dede] transition-colors disabled:opacity-40">{busy === b.id + "cancelled" ? <Loader2 size={15} className="animate-spin" /> : <X size={15} />} Stornieren</button>
                <button disabled={busy} onClick={() => setEditing(b)} data-testid={`edit-${b.id}`} className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#F5F2ED] text-[#5A607A] hover:text-[#2B4BFF] transition-colors" title="Bearbeiten"><Pencil size={15} /></button>
                <button disabled={busy} onClick={() => removeBooking(b.id)} className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#F5F2ED] text-[#5A607A] hover:text-[#C0392B] transition-colors" title="Löschen"><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 font-body text-sm text-[#0B1533]">
              <p className="flex items-center gap-2"><Plane size={15} className="text-[#2B4BFF]" /> {b.pickup}</p>
              <p className="flex items-center gap-2"><MapPin size={15} className="text-[#2B4BFF]" /> {b.dropoff}</p>
              <p className="flex items-center gap-2"><Calendar size={15} className="text-[#5A607A]" /> {b.date} · {b.time}</p>
              <p className="flex items-center gap-2 text-[#5A607A]">{b.vehicle} · {b.passengers}</p>
              <p className="flex items-center gap-2"><Phone size={15} className="text-[#5A607A]" /> <a href={`tel:${b.phone}`} className="hover:text-[#2B4BFF]">{b.phone}</a></p>
              <p className="flex items-center gap-2"><Mail size={15} className="text-[#5A607A]" /> <a href={`mailto:${b.email}`} className="hover:text-[#2B4BFF]">{b.email}</a></p>
              {b.flight && <p className="text-[#5A607A]">Flug: {b.flight}</p>}
              {b.notes && <p className="sm:col-span-2 text-[#5A607A]">Notiz: {b.notes}</p>}
            </div>
            <div className="mt-4 pt-4 border-t border-[#F0EDE7] flex items-center gap-3 flex-wrap">
              <span className="font-body text-xs text-[#5A607A] inline-flex items-center gap-1.5"><UserPlus size={14} /> Fahrer:</span>
              <select value={b.driver?.id || ""} onChange={(e) => assign(b.id, e.target.value)} data-testid={`assign-${b.id}`} className="bg-white border border-[#E9E5DE] rounded-full px-4 py-1.5 font-body text-sm focus:border-[#2B4BFF] focus:outline-none">
                <option value="">— Fahrer wählen —</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              {b.driver && <span className="font-body text-xs text-[#1B7F45] bg-[#E7F8EE] rounded-full px-3 py-1">Zugewiesen: {b.driver.name}</span>}
            </div>
          </div>
        ))}
      </div>
    )}
  </>
);

const CalendarTab = ({ bookings, STATUS }) => {
  const byDate = {};
  bookings.forEach((b) => { (byDate[b.date] = byDate[b.date] || []).push(b); });
  const dates = Object.keys(byDate).sort();
  if (dates.length === 0) return <div className="bg-white rounded-2xl border border-[#E9E5DE] p-12 text-center font-body text-[#5A607A]">Noch keine geplanten Fahrten.</div>;
  return (
    <div className="grid gap-4">
      {dates.map((d) => (
        <div key={d} className="bg-white rounded-2xl border border-[#E9E5DE] p-5" data-testid={`cal-${d}`}>
          <h3 className="font-heading font-semibold text-[#0B1533] mb-3 flex items-center gap-2"><Calendar size={16} className="text-[#2B4BFF]" /> {new Date(d).toLocaleDateString("de-CH", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</h3>
          <div className="space-y-2">
            {[...byDate[d]].sort((a, b) => (a.time || "").localeCompare(b.time || "")).map((b) => (
              <div key={b.id} className="flex items-center gap-3 flex-wrap text-sm font-body border-l-2 border-[#2B4BFF] pl-3 py-1">
                <span className="font-semibold text-[#0B1533] w-12">{b.time}</span>
                <span className="text-[#0B1533]">{b.dropoff}</span>
                <span className="text-[#5A607A]">· {b.name}</span>
                <span className={`text-xs rounded-full px-2 py-0.5 ${STATUS[b.status]?.cls || ""}`}>{STATUS[b.status]?.label}</span>
                {b.driver && <span className="text-xs text-[#5A607A]">🚗 {b.driver.name}</span>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const TIER_LABELS = [5, 10, 15, 20, 25, 30, 35, 40, 50, 55, 60, 65, 70, 75, 80, 85, 90, 100];
const tierRange = (tiers, i) => {
  const from = i === 0 ? 0 : tiers[i - 1].upto;
  return `${from}–${tiers[i].upto} km`;
};

const PricingTab = ({ pricing, setPricing, fees, setFees, api }) => {
  const [rows, setRows] = useState(pricing);
  const [feeForm, setFeeForm] = useState(fees);
  const [saving, setSaving] = useState(false);
  const [savingFee, setSavingFee] = useState(false);
  const [active, setActive] = useState(0);
  useEffect(() => { setRows(pricing); }, [pricing]);
  useEffect(() => { setFeeForm(fees); }, [fees]);

  const updField = (i, key, val) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: key === "name" ? val : parseFloat(val) || 0 } : row)));
  const updTier = (i, ti, val) => setRows((r) => r.map((row, idx) => {
    if (idx !== i) return row;
    const tiers = (row.tiers || []).map((t, k) => (k === ti ? { ...t, price: parseFloat(val) || 0 } : t));
    return { ...row, tiers };
  }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api("/api/admin/pricing", { method: "PUT", body: JSON.stringify({ vehicles: rows }) });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setPricing(data.vehicles);
      toast.success("Preise gespeichert.");
    } catch (e) { toast.error("Speichern fehlgeschlagen."); } finally { setSaving(false); }
  };

  const saveFees = async () => {
    setSavingFee(true);
    try {
      const res = await api("/api/admin/fees", { method: "PUT", body: JSON.stringify(feeForm) });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setFees({ nightPercent: data.nightPercent, nightStart: data.nightStart, nightEnd: data.nightEnd });
      toast.success("Nachtzuschlag gespeichert.");
    } catch (e) { toast.error("Speichern fehlgeschlagen."); } finally { setSavingFee(false); }
  };

  const v = rows[active];
  return (
    <div className="space-y-6" data-testid="pricing-tab">
      {/* Night surcharge */}
      <div className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6">
        <h3 className="font-heading font-semibold text-[#0B1533] flex items-center gap-2 mb-1"><Moon size={18} className="text-[#2B4BFF]" /> Nachtzuschlag</h3>
        <p className="font-body text-sm text-[#5A607A] mb-4">Aufschlag auf den Fahrpreis für Fahrten in der Nacht. Wird automatisch im Buchungsrechner angewendet.</p>
        <div className="grid sm:grid-cols-3 gap-3 max-w-lg">
          <Field label="Aufschlag (%)" type="number" step="1" value={feeForm.nightPercent} onChange={(e) => setFeeForm({ ...feeForm, nightPercent: parseFloat(e.target.value) || 0 })} data-testid="fee-percent" />
          <Field label="Nacht ab (Std.)" type="number" min="0" max="23" value={feeForm.nightStart} onChange={(e) => setFeeForm({ ...feeForm, nightStart: parseInt(e.target.value) || 0 })} data-testid="fee-start" />
          <Field label="Nacht bis (Std.)" type="number" min="0" max="23" value={feeForm.nightEnd} onChange={(e) => setFeeForm({ ...feeForm, nightEnd: parseInt(e.target.value) || 0 })} data-testid="fee-end" />
        </div>
        <button onClick={saveFees} disabled={savingFee} data-testid="save-fees" className="mt-4 inline-flex items-center gap-2 bg-[#0B1533] text-white font-body font-semibold rounded-full px-6 py-2.5 hover:bg-[#1a2647] transition-colors disabled:opacity-50">{savingFee ? <Loader2 size={16} className="animate-spin" /> : <><Save size={16} /> Speichern</>}</button>
      </div>

      {/* Per-vehicle km-tier pricing */}
      <div className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6">
        <p className="font-body text-sm text-[#5A607A] mb-4">Legen Sie für <strong>jedes Fahrzeug</strong> einen Festpreis pro Kilometer-Bereich fest. Für Strecken über 100 km gilt der <strong>Preis/km</strong>. Änderungen wirken sofort auf der Website.</p>
        <div className="flex flex-wrap gap-2 mb-5">
          {rows.map((row, i) => (
            <button key={row.id} onClick={() => setActive(i)} data-testid={`veh-tab-${row.id}`} className={`font-body text-sm rounded-full px-4 py-2 border transition-colors ${active === i ? "bg-[#2B4BFF] text-white border-[#2B4BFF]" : "bg-white text-[#5A607A] border-[#E9E5DE] hover:border-[#2B4BFF]"}`}>{row.name}</button>
          ))}
        </div>
        {v && (
          <>
            <div className="grid sm:grid-cols-[2fr_1fr] gap-3 mb-5">
              <Field label="Fahrzeugname" value={v.name} onChange={(e) => updField(active, "name", e.target.value)} data-testid={`price-name-${v.id}`} />
              <Field label="Preis/km über 100 km (CHF)" type="number" step="0.1" value={v.perKmOver ?? v.perKm ?? 0} onChange={(e) => updField(active, "perKmOver", e.target.value)} data-testid={`price-perkmover-${v.id}`} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {(v.tiers || []).map((t, ti) => (
                <div key={t.upto}>
                  <label className="font-body text-xs text-[#5A607A] mb-1 block">{tierRange(v.tiers, ti)}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 font-body text-xs text-[#9CA0B0]">CHF</span>
                    <input type="number" step="1" value={t.price} onChange={(e) => updTier(active, ti, e.target.value)} data-testid={`tier-${v.id}-${t.upto}`} className="w-full bg-white border border-[#E9E5DE] rounded-xl pl-11 pr-3 py-2.5 font-body text-sm focus:border-[#2B4BFF] focus:outline-none" />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <button onClick={save} disabled={saving} data-testid="save-pricing" className="mt-6 inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-7 py-3 hover:bg-[#1E38D8] transition-colors disabled:opacity-50">{saving ? <Loader2 size={18} className="animate-spin" /> : <><Save size={18} /> Preise speichern</>}</button>
      </div>
    </div>
  );
};

const SOURCE_LABELS = {
  google: "Google", google_ads: "Google Ads", chatgpt: "ChatGPT", gemini: "Gemini", perplexity: "Perplexity",
  copilot: "Copilot", bing: "Bing", duckduckgo: "DuckDuckGo", yahoo: "Yahoo", social: "Social Media",
  direct: "Direkt", referral: "Verweise", other: "Andere",
};
const SOURCE_COLORS = {
  google: "#4285F4", google_ads: "#0F9D58", chatgpt: "#10A37F", gemini: "#8E44AD", perplexity: "#20808D",
  copilot: "#0078D4", bing: "#00809D", direct: "#5A607A", referral: "#C9A24B", social: "#E1306C", other: "#9CA0B0",
};

const AnalyticsTab = ({ analytics, loading }) => {
  if (!analytics) return <p className="font-body text-[#5A607A] flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Lädt Statistik …</p>;
  const { visits, revenue, bookings } = analytics;
  const sources = Object.entries(visits.bySource || {}).sort((a, b) => b[1] - a[1]);
  const maxSource = Math.max(1, ...sources.map(([, c]) => c));
  const maxMonth = Math.max(1, ...(visits.byMonth || []).map((m) => m.count));
  const stat = (label, value, sub) => (
    <div className="bg-white rounded-2xl border border-[#E9E5DE] p-5">
      <p className="font-body text-xs text-[#5A607A] uppercase tracking-wider">{label}</p>
      <p className="font-heading font-bold text-2xl sm:text-3xl text-[#0B1533] mt-1">{value}</p>
      {sub && <p className="font-body text-xs text-[#9CA0B0] mt-1">{sub}</p>}
    </div>
  );
  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stat("Besuche gesamt", visits.total)}
        {stat("Umsatz (offen+best.)", `CHF ${revenue.total.toLocaleString("de-CH")}`, `Bezahlt: CHF ${revenue.paid.toLocaleString("de-CH")}`)}
        {stat("Buchungen", bookings.total, `${bookings.confirmed} bestätigt · ${bookings.pending} offen`)}
        {stat("Umsatz bestätigt", `CHF ${revenue.confirmed.toLocaleString("de-CH")}`)}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6">
          <h3 className="font-heading font-semibold text-[#0B1533] mb-4">Besuche nach Quelle</h3>
          {sources.length === 0 ? <p className="font-body text-sm text-[#5A607A]">Noch keine Besuche erfasst.</p> : (
            <div className="space-y-3">
              {sources.map(([src, count]) => (
                <div key={src} data-testid={`source-${src}`}>
                  <div className="flex justify-between font-body text-sm text-[#0B1533] mb-1"><span>{SOURCE_LABELS[src] || src}</span><span className="font-semibold">{count}</span></div>
                  <div className="h-2 bg-[#F0EDE7] rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(count / maxSource) * 100}%`, background: SOURCE_COLORS[src] || "#2B4BFF" }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6">
          <h3 className="font-heading font-semibold text-[#0B1533] mb-4">Besuche pro Monat</h3>
          {(visits.byMonth || []).length === 0 ? <p className="font-body text-sm text-[#5A607A]">Noch keine Daten.</p> : (
            <div className="flex items-end gap-2 h-48">
              {visits.byMonth.map((m) => (
                <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${m.month}: ${m.count}`}>
                  <span className="font-body text-[10px] text-[#5A607A]">{m.count}</span>
                  <div className="w-full bg-[#2B4BFF] rounded-t" style={{ height: `${(m.count / maxMonth) * 100}%`, minHeight: "4px" }} />
                  <span className="font-body text-[10px] text-[#9CA0B0]">{m.month.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6">
        <h3 className="font-heading font-semibold text-[#0B1533] mb-4">Umsatz pro Monat</h3>
        {(revenue.byMonth || []).length === 0 ? <p className="font-body text-sm text-[#5A607A]">Noch keine Umsätze.</p> : (
          <div className="space-y-2">
            {revenue.byMonth.map((m) => (
              <div key={m.month} className="flex justify-between font-body text-sm border-b border-[#F0EDE7] pb-1.5"><span className="text-[#5A607A]">{m.month}</span><span className="font-semibold text-[#0B1533]">CHF {m.amount.toLocaleString("de-CH")}</span></div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ImagesTab = ({ images, setImages, pricing, api }) => {
  const [form, setForm] = useState({ hero: images.hero || "", vehicles: { ...(images.vehicles || {}) } });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm({ hero: images.hero || "", vehicles: { ...(images.vehicles || {}) } }); }, [images]);
  const setVeh = (id, url) => setForm((f) => ({ ...f, vehicles: { ...f.vehicles, [id]: url } }));
  const save = async () => {
    setSaving(true);
    try {
      const res = await api("/api/admin/images", { method: "PUT", body: JSON.stringify(form) });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setImages({ hero: data.hero, vehicles: data.vehicles });
      toast.success("Bilder gespeichert. Aktualisieren Sie die Website, um sie zu sehen.");
    } catch (e) { toast.error("Speichern fehlgeschlagen."); } finally { setSaving(false); }
  };
  const ImgRow = ({ label, url, onChange, testid }) => (
    <div className="flex items-center gap-4 border-b border-[#F0EDE7] py-3">
      <div className="w-24 h-16 rounded-xl bg-[#F5F2ED] overflow-hidden shrink-0 flex items-center justify-center">
        {url ? <img src={url} alt={label} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} /> : <ImageIcon size={20} className="text-[#9CA0B0]" />}
      </div>
      <div className="flex-1">
        <label className="font-body text-xs text-[#5A607A] mb-1 block">{label}</label>
        <input type="url" placeholder="https://…" value={url} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-white border border-[#E9E5DE] rounded-xl px-3 py-2.5 font-body text-sm focus:border-[#2B4BFF] focus:outline-none" />
      </div>
    </div>
  );
  return (
    <div className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6 max-w-3xl" data-testid="images-tab">
      <p className="font-body text-sm text-[#5A607A] mb-4">Fügen Sie eine Bild-URL ein, um das Startseiten-Bild oder die Fahrzeugbilder zu ersetzen. Feld leer lassen, um das Standardbild zu behalten.</p>
      <ImgRow label="Startseite (Hero-Bild)" url={form.hero} onChange={(v) => setForm({ ...form, hero: v })} testid="img-hero" />
      {pricing.map((veh) => (
        <ImgRow key={veh.id} label={`Fahrzeug: ${veh.name}`} url={form.vehicles[veh.id] || ""} onChange={(v) => setVeh(veh.id, v)} testid={`img-veh-${veh.id}`} />
      ))}
      <button onClick={save} disabled={saving} data-testid="save-images" className="mt-5 inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-7 py-3 hover:bg-[#1E38D8] transition-colors disabled:opacity-50">{saving ? <Loader2 size={18} className="animate-spin" /> : <><Save size={18} /> Bilder speichern</>}</button>
    </div>
  );
};

const EditBookingModal = ({ booking, onClose, onSave, busy, pricing }) => {
  const [f, setF] = useState({
    name: booking.name || "", email: booking.email || "", phone: booking.phone || "",
    pickup: booking.pickup || "", dropoff: booking.dropoff || "", date: booking.date || "", time: booking.time || "",
    vehicle: booking.vehicle || "", passengers: booking.passengers || "", flight: booking.flight || "", notes: booking.notes || "", price: booking.price || "",
  });
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="edit-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl my-8 p-6 sm:p-7">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-bold text-xl text-[#0B1533]">Buchung bearbeiten <span className="font-body text-sm text-[#9CA0B0]">Nr. {booking.ref}</span></h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#F5F2ED] text-[#5A607A] hover:text-[#0B1533] flex items-center justify-center"><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Name" value={f.name} onChange={(e) => upd("name", e.target.value)} data-testid="edit-name" />
          <Field label="Telefon" value={f.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="edit-phone" />
          <Field label="E-Mail" type="email" value={f.email} onChange={(e) => upd("email", e.target.value)} data-testid="edit-email" />
          <Field label="Flugnummer" value={f.flight} onChange={(e) => upd("flight", e.target.value)} data-testid="edit-flight" />
          <Field label="Abholort" value={f.pickup} onChange={(e) => upd("pickup", e.target.value)} data-testid="edit-pickup" />
          <Field label="Ziel" value={f.dropoff} onChange={(e) => upd("dropoff", e.target.value)} data-testid="edit-dropoff" />
          <Field label="Datum" type="date" value={f.date} onChange={(e) => upd("date", e.target.value)} data-testid="edit-date" />
          <Field label="Uhrzeit" type="time" value={f.time} onChange={(e) => upd("time", e.target.value)} data-testid="edit-time" />
          <div>
            <label className="font-body text-xs text-[#5A607A] mb-1 block">Fahrzeug</label>
            <select value={f.vehicle} onChange={(e) => upd("vehicle", e.target.value)} data-testid="edit-vehicle" className="w-full bg-white border border-[#E9E5DE] rounded-xl px-3 py-2.5 font-body text-sm focus:border-[#2B4BFF] focus:outline-none">
              <option value="">— Fahrzeug —</option>
              {pricing.map((v) => <option key={v.id} value={v.name}>{v.name}</option>)}
            </select>
          </div>
          <Field label="Personen" value={f.passengers} onChange={(e) => upd("passengers", e.target.value)} data-testid="edit-pax" />
          <Field label="Preis (CHF)" value={f.price} onChange={(e) => upd("price", e.target.value)} data-testid="edit-price" />
        </div>
        <div className="mt-3">
          <label className="font-body text-xs text-[#5A607A] mb-1 block">Notizen</label>
          <textarea value={f.notes} onChange={(e) => upd("notes", e.target.value)} data-testid="edit-notes" rows={2} className="w-full bg-white border border-[#E9E5DE] rounded-xl px-3 py-2.5 font-body text-sm focus:border-[#2B4BFF] focus:outline-none" />
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="font-body text-sm font-semibold rounded-full px-6 py-2.5 border border-[#E9E5DE] text-[#5A607A] hover:border-[#0B1533] transition-colors">Abbrechen</button>
          <button onClick={() => onSave(booking.id, f)} disabled={busy} data-testid="edit-save" className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-6 py-2.5 hover:bg-[#1E38D8] transition-colors disabled:opacity-50">{busy === booking.id + "edit" ? <Loader2 size={16} className="animate-spin" /> : <><Save size={16} /> Speichern</>}</button>
        </div>
      </div>
    </div>
  );
};

const DriversTab = ({ drivers, setDrivers, api }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [adding, setAdding] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const res = await api("/api/admin/drivers", { method: "POST", body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setDrivers((d) => [...d, data]);
      setForm({ name: "", email: "", phone: "" });
      toast.success("Fahrer hinzugefügt.");
    } catch (e) { toast.error("Hinzufügen fehlgeschlagen."); } finally { setAdding(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Fahrer löschen?")) return;
    try {
      const res = await api(`/api/admin/drivers/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      setDrivers((d) => d.filter((x) => x.id !== id));
      toast.success("Fahrer gelöscht.");
    } catch (e) { toast.error("Löschen fehlgeschlagen."); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="drivers-tab">
      <form onSubmit={add} className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6 space-y-3 h-fit">
        <h3 className="font-heading font-semibold text-[#0B1533]">Neuer Fahrer</h3>
        <Field label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="driver-name" />
        <Field label="E-Mail" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="driver-email" />
        <Field label="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="driver-phone" />
        <button type="submit" disabled={adding} data-testid="add-driver" className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-6 py-2.5 hover:bg-[#1E38D8] transition-colors disabled:opacity-50">{adding ? <Loader2 size={16} className="animate-spin" /> : "Hinzufügen"}</button>
      </form>
      <div className="space-y-3">
        {drivers.length === 0 ? <div className="bg-white rounded-2xl border border-[#E9E5DE] p-8 text-center font-body text-[#5A607A]">Noch keine Fahrer.</div> : drivers.map((d) => (
          <div key={d.id} className="bg-white rounded-2xl border border-[#E9E5DE] p-4 flex items-center justify-between" data-testid={`driver-${d.id}`}>
            <div><p className="font-heading font-semibold text-[#0B1533]">{d.name}</p><p className="font-body text-xs text-[#5A607A]">{d.email} · {d.phone}</p></div>
            <button onClick={() => del(d.id)} className="w-9 h-9 rounded-full bg-[#F5F2ED] text-[#5A607A] hover:text-[#C0392B] transition-colors flex items-center justify-center"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const SettingsTab = ({ api }) => {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api("/api/admin/change-password", { method: "POST", body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setForm({ current_password: "", new_password: "" });
      toast.success("Passwort geändert.");
    } catch (err) { toast.error(typeof err.message === "string" ? err.message : "Fehlgeschlagen"); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6 max-w-md space-y-3" data-testid="settings-tab">
      <h3 className="font-heading font-semibold text-[#0B1533]">Passwort ändern</h3>
      <Field label="Aktuelles Passwort" type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="current-password" />
      <Field label="Neues Passwort (min. 6 Zeichen)" type="password" required value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="new-password" />
      <button type="submit" disabled={saving} data-testid="save-password" className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-6 py-2.5 hover:bg-[#1E38D8] transition-colors disabled:opacity-50">{saving ? <Loader2 size={16} className="animate-spin" /> : "Passwort ändern"}</button>
    </form>
  );
};

export default Admin;
